import type { PageHelpFlow } from './types.js';

type FlowSteps = PageHelpFlow['steps'];

const JOURNAL_STEPS: FlowSteps = [
  { id: 'draft', label: 'پیش‌نویس' },
  { id: 'submitted', label: 'ارسال برای تأیید' },
  { id: 'approved', label: 'تأیید سند' },
  { id: 'posted', label: 'ثبت قطعی' },
  { id: 'reports', label: 'کنترل گزارش‌ها' },
];

const SALES_STEPS: FlowSteps = [
  { id: 'sale', label: 'سفارش، تحویل و فاکتور' },
  { id: 'receivable', label: 'طلب و سند فروش' },
  { id: 'journal', label: 'بررسی و قطعی‌کردن سند' },
  { id: 'receipt', label: 'دریافت و تخصیص' },
  { id: 'control', label: 'کنترل مانده و گزارش' },
];

const PURCHASE_STEPS: FlowSteps = [
  { id: 'purchase', label: 'سفارش و دریافت خرید' },
  { id: 'payable', label: 'بدهی و سند مرتبط' },
  { id: 'journal', label: 'بررسی و قطعی‌کردن سند' },
  { id: 'payment', label: 'پرداخت و تخصیص' },
  { id: 'control', label: 'کنترل مانده و گزارش' },
];

const RECEIPT_STEPS: FlowSteps = [
  { id: 'source', label: 'بررسی طلب و مدرک دریافت' },
  { id: 'treasury', label: 'ثبت دریافت و تخصیص' },
  { id: 'journal', label: 'بررسی سند پیش‌نویس' },
  { id: 'posted', label: 'تأیید و ثبت قطعی سند' },
  { id: 'control', label: 'کنترل تسویه و بانک' },
];

const PAYMENT_STEPS: FlowSteps = [
  { id: 'source', label: 'بررسی بدهی و مدرک پرداخت' },
  { id: 'treasury', label: 'ثبت پرداخت و تخصیص' },
  { id: 'journal', label: 'بررسی سند پیش‌نویس' },
  { id: 'posted', label: 'تأیید و ثبت قطعی سند' },
  { id: 'control', label: 'کنترل تسویه و بانک' },
];

const SALES_ACCOUNT_STEPS: FlowSteps = [
  { id: 'accounts', label: 'تعریف حساب‌های قابل ثبت' },
  { id: 'settings', label: 'تعیین مقصد حسابداری فروش' },
  { id: 'invoice', label: 'صدور فاکتور فروش' },
  { id: 'journal', label: 'بررسی و ثبت قطعی سند' },
  { id: 'reports', label: 'گزارش فروش و مالی' },
];

const PURCHASE_ACCOUNT_STEPS: FlowSteps = [
  { id: 'accounts', label: 'تعریف حساب‌های قابل ثبت' },
  { id: 'purchase', label: 'مدرک خرید و بدهی' },
  { id: 'journal', label: 'ثبت و بررسی سند مرتبط' },
  { id: 'payment', label: 'پرداخت بدهی' },
  { id: 'reports', label: 'کنترل هزینه و مانده' },
];

const TREASURY_ACCOUNT_STEPS: FlowSteps = [
  { id: 'accounts', label: 'حساب‌های کدینگ' },
  { id: 'treasury-account', label: 'تعریف بانک یا صندوق' },
  { id: 'transaction', label: 'دریافت یا پرداخت' },
  { id: 'journal', label: 'بررسی و ثبت قطعی سند' },
  { id: 'control', label: 'کنترل بانک و گزارش' },
];

const REPORT_STEPS: FlowSteps = [
  { id: 'source', label: 'عملیات و مدارک اولیه' },
  { id: 'draft', label: 'ایجاد و بررسی سند' },
  { id: 'posted', label: 'تأیید و ثبت قطعی' },
  { id: 'report', label: 'گزارش با تاریخ و دفتر مشخص' },
  { id: 'follow-up', label: 'پیگیری اختلاف و تصمیم' },
];

const RECORD_STAGE_NOTE =
  'جای برجسته‌شده، نقش این صفحه در فرایند است؛ وضعیت واقعی هر رکورد را از نشان وضعیت همان رکورد بخوان.';

function at(
  id: string,
  title: string,
  steps: FlowSteps,
  currentStepId: string,
  position: string,
  before: string,
  after: string,
  role: 'stage' | 'support' = 'stage',
  note?: string,
): PageHelpFlow {
  return {
    id,
    title,
    steps,
    role,
    currentStepId,
    position,
    before,
    after,
    ...(note ? { note } : {}),
  };
}

function accountFlows(detail: boolean): readonly PageHelpFlow[] {
  const position = detail
    ? 'این صفحه محل بررسی یک حساب، زیرحساب‌ها و قواعد تفصیلی آن است؛ پیش از استفاده از همان حساب در عملیات.'
    : 'این صفحه زیرساخت مشترک ثبت‌های مالی است؛ حساب مناسب باید پیش از استفاده در سند یا تنظیمات انتخاب و قابل ثبت باشد.';
  const before = detail
    ? 'حساب موردنظر باید از کدینگ انتخاب شده باشد و ماهیت و کاربرد آن با مسئول مالی روشن باشد.'
    : 'ساختار حساب‌ها، سطح حساب و کاربرد هر حساب باید با مسئول مالی مشخص شده باشد.';
  return [
    at(
      'accounts-for-sales',
      'آماده‌سازی حساب‌ها برای فروش',
      SALES_ACCOUNT_STEPS,
      'accounts',
      position,
      before,
      'حساب‌های مناسب را در «حساب‌های فروش و پورسانت» تعیین کن؛ سپس فاکتور و سند مرتبط از مسیر فروش پیگیری می‌شوند.',
      'support',
      'تعریف یا تغییر یک حساب، فاکتور یا سندی ایجاد نمی‌کند و به‌تنهایی اسناد قبلی را اصلاح نمی‌کند.',
    ),
    at(
      'accounts-for-purchase',
      'آماده‌سازی حساب‌ها برای خرید و بدهی',
      PURCHASE_ACCOUNT_STEPS,
      'accounts',
      position,
      before,
      'در ثبت بدهی یا سند خرید، حساب‌های مرتبط را بر اساس مدرک و مسیر موجود انتخاب و سند ایجادشده را بررسی کن.',
      'support',
      'ثبت سفارش خرید به‌تنهایی معادل ثبت قطعی بدهی نیست؛ وجود بدهی و سند مرتبط را در بخش مالی کنترل کن.',
    ),
    at(
      'accounts-for-treasury',
      'آماده‌سازی حساب‌ها برای خزانه',
      TREASURY_ACCOUNT_STEPS,
      'accounts',
      position,
      before,
      'حساب بانک یا صندوق را در خزانه به حساب مناسب کدینگ وصل کن؛ دریافت و پرداخت پس از ثبت، سند پیش‌نویس خود را دارند.',
      'support',
      'حساب کدینگ با حساب خزانه فرق دارد؛ دیدن نام بانک در کدینگ به معنی تعریف‌شدن آن در خزانه نیست.',
    ),
  ];
}

function sourceJournalFlows(detail: boolean): readonly PageHelpFlow[] {
  const position = detail
    ? 'اینجا جزئیات سند مرتبط را بررسی و مرحلهٔ مجاز آن را انجام می‌دهی؛ همهٔ مراحل بررسی یک سند ممکن است روی همین صفحه دیده شوند.'
    : 'اینجا سند مرتبط با عملیات را پیدا می‌کنی و پیش از ادامه، منبع و وضعیت آن را بررسی می‌کنی.';
  return [
    at(
      'sales-journal',
      'از فاکتور فروش تا کنترل وصول',
      SALES_STEPS,
      'journal',
      position,
      'فاکتور فروش و سند درآمد و طلب مرتبط باید ایجاد شده باشند؛ شماره و مبلغ آن‌ها را با هم تطبیق بده.',
      'پس از بررسی، سند مراحل ارسال برای تأیید، تأیید و ثبت قطعی را طی می‌کند؛ دریافت وجه و تخصیص آن از خزانه پیگیری می‌شود.',
      'support',
      `${RECORD_STAGE_NOTE} برای فاکتوری که سند مرتبط دارد، سند دستی تکراری نساز.`,
    ),
    at(
      'purchase-journal',
      'از خرید تا کنترل پرداخت',
      PURCHASE_STEPS,
      'journal',
      position,
      'مدرک خرید، بدهی و سند مرتبط ایجادشده را بررسی کن؛ صرف وجود سفارش یا رسید را دلیل ثبت قطعی بدهی نگیر.',
      'سند معتبر مراحل تأیید و ثبت قطعی را طی می‌کند؛ پرداخت و تخصیص آن به بدهی از خزانه انجام و مانده دوباره کنترل می‌شود.',
      'support',
      `${RECORD_STAGE_NOTE} این نمودار، ساخت خودکار سند برای هر نوع مدرک خرید را وعده نمی‌دهد.`,
    ),
    at(
      'treasury-journal',
      'از دریافت یا پرداخت تا گزارش مالی',
      [
        { id: 'source', label: 'مدرک دریافت یا پرداخت' },
        { id: 'treasury', label: 'ثبت در خزانه' },
        { id: 'journal', label: 'بررسی سند پیش‌نویس' },
        { id: 'posted', label: 'تأیید و ثبت قطعی' },
        { id: 'control', label: 'کنترل مانده و گزارش' },
      ],
      'journal',
      position,
      'گردش دریافت یا پرداخت در خزانه ثبت شده و سند پیش‌نویس مرتبط در دسترس است.',
      'سند را با مدرک و تخصیص‌ها مقایسه کن؛ پس از طی مراحل مجاز و ثبت قطعی، نتیجه را در گردش خزانه و گزارش مربوط کنترل کن.',
      'support',
      `${RECORD_STAGE_NOTE} پیش‌نویس خزانه هنوز ثبت قطعی دفتر نیست.`,
    ),
  ];
}

function treasuryFlows(detail: boolean): readonly PageHelpFlow[] {
  return [
    at(
      'customer-receipt',
      'وصول طلب مشتری',
      RECEIPT_STEPS,
      'treasury',
      detail
        ? 'این صفحه مشخصات دریافت ثبت‌شده و تخصیص آن به طلب‌ها را نشان می‌دهد؛ محل خواندن ارتباط‌هاست.'
        : 'اینجا دریافت واقعی را ثبت و در صورت تسویه، آن را به طلب‌های همان طرف‌حساب تخصیص می‌دهی.',
      'طلب، طرف‌حساب، حساب خزانه و مدرک دریافت را بررسی کن؛ برای دریافت بدون تسویه هم حساب مقابل لازم است.',
      detail
        ? 'از لینک سند، مرحلهٔ بررسی و ثبت قطعی را دنبال کن؛ سپس ماندهٔ طلب و صورتحساب بانک را کنترل کن.'
        : 'سند پیش‌نویس دریافت را بررسی کن و مراحل تأیید و ثبت قطعی آن را دنبال کن؛ بعد ماندهٔ مشتری را ببین.',
      detail ? 'support' : 'stage',
      `${RECORD_STAGE_NOTE} جمع تخصیص‌ها باید با مبلغ دریافت برابر باشد؛ ثبت در ERP، واریز بانکی انجام نمی‌دهد.`,
    ),
    at(
      'supplier-payment',
      'پرداخت بدهی تأمین‌کننده',
      PAYMENT_STEPS,
      'treasury',
      detail
        ? 'این صفحه ارتباط پرداخت ثبت‌شده با سند و بدهی‌های تخصیص‌یافته را روشن می‌کند.'
        : 'اینجا پرداخت واقعی را ثبت و در صورت تسویه، مبلغ را بین بدهی‌های همان طرف‌حساب تخصیص می‌دهی.',
      'مدرک و مجوز پرداخت، بدهی، طرف‌حساب و بانک یا صندوق مربوط را کنترل کن.',
      detail
        ? 'سند مرتبط را باز کن؛ پس از طی مراحل مجاز و ثبت قطعی، ماندهٔ بدهی و ثبت بانک را دوباره بررسی کن.'
        : 'سند پیش‌نویس پرداخت را بررسی و مراحل قطعی‌شدن آن را پیگیری کن؛ سپس ماندهٔ تأمین‌کننده را کنترل کن.',
      detail ? 'support' : 'stage',
      `${RECORD_STAGE_NOTE} ثبت پرداخت در نرم‌افزار، دستور انتقال واقعی پول به بانک نیست.`,
    ),
  ];
}

function financialReport(
  id: string,
  title: string,
  position: string,
  before: string,
  after: string,
  note?: string,
): readonly PageHelpFlow[] {
  return [
    at(
      id,
      title,
      REPORT_STEPS,
      'report',
      position,
      before,
      after,
      'stage',
      note ??
        'این گزارش از اسناد قطعی ساخته می‌شود؛ پیش‌نویس و سند در انتظار تأیید را در جمع قطعی آن جست‌وجو نکن.',
    ),
  ];
}

/** Explicit page bindings: these diagrams describe workflow roles, never live record progress. */
export const FINANCE_PAGE_FLOWS: Readonly<Record<string, readonly PageHelpFlow[]>> = {
  'finance-overview': [
    {
      id: 'finance-workspace',
      title: 'چرخهٔ کار مالی شرکت',
      role: 'overview',
      steps: [
        { id: 'prepare', label: 'دفتر، دوره و حساب‌ها' },
        { id: 'operations', label: 'فروش، خرید و خزانه' },
        { id: 'journals', label: 'بررسی و ثبت قطعی اسناد' },
        { id: 'settle', label: 'پیگیری وصول و پرداخت' },
        { id: 'reports', label: 'گزارش و بستن دوره' },
      ],
      position:
        'این صفحه نقشهٔ ورودی کارهای مالی است؛ یک مرحلهٔ عملیاتی واحد یا وضعیت یک سند را نشان نمی‌دهد.',
      before: 'باید وارد فضای کاری درست شده باشی و دفتر و دسترسی مرتبط با کارت مشخص باشد.',
      after: 'متناسب با سؤال خودت، وارد سند، دریافتنی، پرداختنی، خزانه یا گزارش مربوط شو.',
      note: 'مسیرها به نوع عملیات وابسته‌اند؛ برای نمونه دریافت نقدی بدون طلب قبلی هم ممکن است. اعداد این دفتر را با دفتر دیگری مخلوط نکن.',
    },
  ],
  'finance-journals': [
    at(
      'journal-lifecycle',
      'چرخهٔ وضعیت سند حسابداری',
      JOURNAL_STEPS,
      'draft',
      'این صفحه محل پیدا کردن سندها و ساخت پیش‌نویس دستی است؛ فهرست می‌تواند سندهایی با وضعیت‌های متفاوت داشته باشد.',
      'دفتر، دورهٔ باز، حساب‌های مجاز و مدرک عملیات لازم‌اند؛ اول بررسی کن سند قبلاً از فروش، خرید یا خزانه ساخته نشده باشد.',
      'جزئیات سند را باز کن تا حساب‌ها و مبلغ‌ها بررسی و مراحل ارسال برای تأیید، تأیید و ثبت قطعی انجام شوند.',
      'stage',
      RECORD_STAGE_NOTE,
    ),
    ...sourceJournalFlows(false),
  ],
  'finance-journal-detail': [
    at(
      'journal-lifecycle',
      'چرخهٔ وضعیت سند حسابداری',
      JOURNAL_STEPS,
      'submitted',
      'این صفحه محل کنترل سطرها و انجام مرحلهٔ مجاز یک سند است؛ پیش‌نویس، ارسال‌شده، تأییدشده و قطعی همگی می‌توانند همین‌جا دیده شوند.',
      'سند باید ساخته شده باشد؛ دفتر، تاریخ، مدرک منبع و تراز بودن سطرها را کنترل کن.',
      'اقدام مجاز بعدی را مطابق وضعیت و دسترسی انجام بده؛ پس از ثبت قطعی، نتیجه در گزارش‌های مربوط کنترل می‌شود.',
      'support',
      `${RECORD_STAGE_NOTE} دیدن این صفحه به معنی ارسال یا تأییدشدن سند نیست.`,
    ),
    ...sourceJournalFlows(true),
    at(
      'posted-journal-correction',
      'اصلاح یک سند قطعی',
      [
        { id: 'source', label: 'یافتن سند قطعی اشتباه' },
        { id: 'reversal', label: 'ثبت درخواست سند برگشت' },
        { id: 'review', label: 'بررسی سند معکوس' },
        { id: 'posted', label: 'قطعی‌کردن سند معکوس' },
        { id: 'control', label: 'کنترل نتیجهٔ اصلاح' },
      ],
      'reversal',
      'در جزئیات سند قطعی، مسیر مجاز برگشت را آغاز و سند برگشت مرتبط را دنبال می‌کنی.',
      'اصل سند باید قطعی باشد و علت اصلاح و تاریخ مجاز برگشت بررسی شده باشد.',
      'وضعیت سند برگشت و سپس اثر آن را روی اسناد و گزارش‌های مرتبط بررسی کن؛ در صورت نیاز، ثبت درستِ جایگزین را با مسئول مالی پیگیری کن.',
      'support',
      'سند قطعی ویرایش یا حذف نمی‌شود؛ برای سندهای دارای منبع فروش، خرید یا خزانه، اصلاح منبع و آثار وابسته هم باید از مسیر مجاز همان عملیات بررسی شود.',
    ),
  ],
  'finance-accounts': accountFlows(false),
  'finance-account-detail': accountFlows(true),
  'finance-receivables': [
    at(
      'sales-receivable',
      'از فروش تا وصول طلب',
      SALES_STEPS,
      'receivable',
      'این صفحه محل بررسی طلب‌های باز و سررسید مشتریان است؛ هنوز مرحلهٔ ثبت دریافت وجه نیست.',
      'طلب باید از منبع فروش یا ثبت دستی معتبر ایجاد شده باشد؛ طرف‌حساب و سند منبع را کنترل کن.',
      'سند مرتبط را برای بررسی و ثبت قطعی دنبال کن؛ برای وصول، دریافت خزانه را به قلم درست تخصیص بده و مانده را دوباره ببین.',
      'stage',
      'نمودار مسیر معمول فروش را نشان می‌دهد؛ طلب دستی هم ممکن است. دیدن قلم در فهرست به معنی قطعی‌بودن سند یا وصول وجه نیست.',
    ),
  ],
  'finance-receivable-detail': [
    at(
      'sales-receivable-detail',
      'پیگیری یک طلب تا تسویه',
      SALES_STEPS,
      'receivable',
      'اینجا روی یک طلب مشخص تمرکز می‌کنی و مبلغ اولیه، مانده، سررسید و سند منبعش را می‌خوانی.',
      'قلم درست باید از دریافتنی‌ها انتخاب شده و طرف‌حساب و شمارهٔ منبع آن مشخص باشد.',
      'برای بررسی ثبت اولیه به سند مرتبط برو؛ برای تخصیص دریافت، از خزانه استفاده و نتیجهٔ مانده را در همین قلم کنترل کن.',
      'support',
      'این صفحه خودش دریافت یا تخصیص ثبت نمی‌کند؛ جزئیات تخصیص در گردش خزانه است. ماندهٔ این قلم با ماندهٔ کل مشتری فرق دارد.',
    ),
  ],
  'finance-payables': [
    at(
      'purchase-payable',
      'از خرید تا تسویهٔ بدهی',
      PURCHASE_STEPS,
      'payable',
      'اینجا بدهی‌های باز و سررسید تأمین‌کنندگان را کنترل می‌کنی؛ این مرحله با پرداخت پول فرق دارد.',
      'مدرک خرید و بدهی ایجادشده یا ثبت دستی معتبر لازم است؛ صرف سفارش یا رسید را معادل بدهی قطعی نگیر.',
      'سند مرتبط را بررسی کن؛ پرداخت واقعی و تخصیص آن به بدهی از خزانه انجام می‌شود و بعد مانده کنترل می‌شود.',
      'stage',
      'وجود بدهی و سند مرتبط را از رکورد واقعی بررسی کن؛ این راهنما ایجاد خودکار بدهی از هر نوع مدرک خرید را فرض نمی‌کند.',
    ),
  ],
  'finance-payable-detail': [
    at(
      'purchase-payable-detail',
      'پیگیری یک بدهی تا تسویه',
      PURCHASE_STEPS,
      'payable',
      'اینجا مبلغ اولیه، مبلغ تسویه‌شده و ماندهٔ یک بدهی مشخص را کنار سند منبع می‌بینی.',
      'بدهی موردنظر باید ایجاد شده باشد؛ تأمین‌کننده و شمارهٔ مدرک را با اصل آن مقایسه کن.',
      'ثبت اولیه را از لینک سند بررسی کن؛ پرداخت و تخصیص را در خزانه پیگیری و ماندهٔ همین بدهی را دوباره کنترل کن.',
      'support',
      'این صفحه محل ثبت پرداخت نیست؛ صرف وجود یک پرداخت به نام تأمین‌کننده، تسویهٔ این قلم را ثابت نمی‌کند.',
    ),
  ],
  'finance-treasury': treasuryFlows(false),
  'finance-treasury-detail': treasuryFlows(true),
  'finance-cheques': [
    at(
      'inward-cheque',
      'پیگیری چک دریافتی',
      [
        { id: 'source', label: 'بررسی اصل چک و طرف‌حساب' },
        { id: 'cheque', label: 'ثبت و پیگیری چک دریافتی' },
        { id: 'event', label: 'ثبت اتفاق واقعی چک' },
        { id: 'journal', label: 'کنترل سند و وضعیت ثبت' },
        { id: 'control', label: 'کنترل مانده و بانک' },
      ],
      'cheque',
      'این صفحه محل ثبت مشخصات چک و دنبال کردن رویدادهای مجاز آن در وضعیت‌های مختلف است.',
      'اصل چک، مبلغ، صیاد، سررسید، طرف‌حساب و حساب‌های لازم باید کنترل شده باشند.',
      'فقط اتفاقی را که واقعاً رخ داده ثبت کن؛ سپس وضعیت چک، سندهای مرتبط و نتیجهٔ مانده را بررسی کن.',
      'stage',
      `${RECORD_STAGE_NOTE} ثبت در ERP جای ثبت یا استعلام در سامانهٔ بانکی را نمی‌گیرد و دریافت چک به معنی وصول وجه آن نیست.`,
    ),
    at(
      'outward-cheque',
      'پیگیری چک پرداختی',
      [
        { id: 'source', label: 'بررسی بدهی و مجوز پرداخت' },
        { id: 'cheque', label: 'ثبت مشخصات چک پرداختی' },
        { id: 'event', label: 'پیگیری سررسید و وضعیت چک' },
        { id: 'journal', label: 'کنترل سند مرتبط' },
        { id: 'control', label: 'کنترل بدهی و بانک' },
      ],
      'cheque',
      'در همین صفحه چک پرداختی و در صورت استفاده، دسته‌چک مربوط را ثبت و وضعیتش را پیگیری می‌کنی.',
      'مدرک و مجوز پرداخت، حساب بانکی و مشخصات برگ چک باید بررسی شده باشند.',
      'رویدادهای مجاز را مطابق وضعیت واقعی پیگیری کن؛ اثر اسناد و ماندهٔ بدهی را با ثبت بانک مقایسه کن.',
      'stage',
      'صدور چک در نرم‌افزار به معنی برداشت قطعی بانک نیست؛ هر اقدام فقط در وضعیت و دسترسی مجاز انجام می‌شود.',
    ),
  ],
  'finance-bank-reconciliation': [
    at(
      'bank-reconciliation',
      'کنترل ثبت‌های شرکت با بانک',
      [
        { id: 'treasury', label: 'ثبت و کنترل گردش خزانه' },
        { id: 'statement', label: 'آماده‌کردن صورتحساب بانک' },
        { id: 'match', label: 'تطبیق گردش‌ها' },
        { id: 'reconcile', label: 'ثبت صورت مغایرت' },
        { id: 'follow-up', label: 'پیگیری اقلام باز' },
      ],
      'match',
      'اینجا گردش واردشدهٔ بانک را با ثبت‌های خزانه تطبیق می‌دهی و صورت مغایرت را تهیه می‌کنی.',
      'حساب بانکی درست، صورتحساب همان بازه و ثبت‌های مربوط خزانه باید در دسترس باشند.',
      'نتیجهٔ تطبیق و صورت مغایرت را بررسی کن؛ موارد باز را با مدرک و مسیر اصلاح مجاز پیگیری کن.',
      'stage',
      'تطبیق‌دادن دو گردش، سند اصلاحی ایجاد نمی‌کند و انتقال پولی انجام نمی‌دهد. مرحلهٔ برجسته نقش صفحه است، نه وضعیت یک ردیف بانک.',
    ),
  ],
  'finance-sales-posting-accounts': [
    at(
      'sales-posting-setup',
      'آماده‌سازی سند فاکتور فروش',
      SALES_ACCOUNT_STEPS,
      'settings',
      'اینجا مقصد حسابداری دریافتنی، درآمد و مالیات فاکتور را تعیین می‌کنی؛ پیش از صدور فاکتور.',
      'حساب‌های مناسب و قابل ثبت باید در کدینگ تعریف شده و انتخابشان به تأیید مسئول مالی رسیده باشد.',
      'فروشنده از مسیر سفارش، فاکتور را صادر می‌کند؛ سند درآمد و طلب مرتبط برای بررسی مالی دنبال می‌شوند.',
      'support',
      'ذخیرهٔ تنظیمات به‌تنهایی فاکتور یا سند ایجاد نمی‌کند و نباید به‌عنوان اصلاح اسناد قبلی تلقی شود.',
    ),
    at(
      'commission-posting-setup',
      'آماده‌سازی حساب‌های پورسانت',
      [
        { id: 'accounts', label: 'تعریف حساب‌های هزینه و بدهی' },
        { id: 'settings', label: 'تعیین حساب‌های پورسانت' },
        { id: 'commission', label: 'محاسبه و بررسی پورسانت' },
        { id: 'journal', label: 'پیگیری سند مرتبط' },
        { id: 'control', label: 'کنترل پرداخت و گزارش' },
      ],
      'settings',
      'این صفحه تنظیم حساب هزینهٔ پورسانت و پورسانت پرداختنی را از کار فروشنده جدا نگه می‌دارد.',
      'حساب‌های مناسب و قواعد محاسبهٔ پورسانت باید توسط مسئولان مربوط مشخص شده باشند.',
      'محاسبه و سند واقعی پورسانت را از بخش مربوط پیگیری کن؛ برای پرداخت، مدرک و بدهی قابل تسویه را بررسی کن.',
      'support',
      'انتخاب حساب، محاسبه یا پرداخت خودکار پورسانت انجام نمی‌دهد؛ ایجادشدن و وضعیت هر سند را از رکورد واقعی بخوان.',
    ),
  ],
  'finance-vat': [
    at(
      'vat-period',
      'کنترل ارزش افزودهٔ دوره',
      [
        { id: 'operations', label: 'خرید و فروش دوره' },
        { id: 'journals', label: 'کنترل ثبت‌های قطعی' },
        { id: 'vat', label: 'بررسی و تهیهٔ اظهارنامه' },
        { id: 'filing', label: 'پیگیری تسلیم و رهگیری' },
        { id: 'payment', label: 'ثبت مدرک پرداخت' },
      ],
      'vat',
      'اینجا ثبت‌های ارزش افزوده و خلاصهٔ دوره را کنترل و وضعیت اظهارنامه را پیگیری می‌کنی.',
      'دفتر قانونی، بازهٔ مالیاتی و ثبت‌های قطعی خرید و فروش مرتبط باید بررسی شده باشند.',
      'رهگیری تسلیم و مدرک پرداخت واقعی را طبق مسیر مجاز ثبت و وضعیت هر اظهارنامه را کنترل کن.',
      'stage',
      'تهیه یا ثبت وضعیت اظهارنامه در ERP به‌تنهایی به معنی پذیرش سازمان مالیاتی نیست؛ این صفحه چند مرحلهٔ اظهارنامه را نشان می‌دهد.',
    ),
  ],
  'finance-moadian': [
    at(
      'moadian-submission',
      'از صورتحساب فروش تا پاسخ مودیان',
      [
        { id: 'invoice', label: 'صورتحساب و اطلاعات معتبر' },
        { id: 'connection', label: 'تنظیم اتصال دفتر قانونی' },
        { id: 'submit', label: 'ارسال صورتحساب' },
        { id: 'inquiry', label: 'استعلام نتیجهٔ سامانه' },
        { id: 'follow-up', label: 'پیگیری پاسخ و اصلاح مجاز' },
      ],
      'submit',
      'این صفحه محل تنظیم اتصال، ارسال و پیگیری پاسخ صورتحساب‌هاست؛ ارسال و استعلام دو مرحلهٔ متفاوت‌اند.',
      'دفتر قانونی، صورتحساب معتبر، شناسه‌های لازم و تنظیم درست محیط آزمایشی یا عملیاتی لازم‌اند.',
      'شمارهٔ رهگیری و نتیجهٔ استعلام را کنترل کن؛ خطا یا نیاز به اصلاح را از مسیر مجاز صورتحساب ارجاعی پیگیری کن.',
      'stage',
      `${RECORD_STAGE_NOTE} ارسال یا حالت «در انتظار پردازش» تأیید نهایی سامانه نیست؛ صورتحساب ارسال‌شده مانند پیش‌نویس ویرایش نمی‌شود.`,
    ),
  ],
  'finance-books': [
    at(
      'accounting-book-setup',
      'انتخاب دفتر برای ثبت عملیات',
      [
        { id: 'purpose', label: 'تعیین نیاز قانونی یا مدیریتی' },
        { id: 'book', label: 'انتخاب دفتر حسابداری' },
        { id: 'period', label: 'کنترل دوره و حساب‌ها' },
        { id: 'journal', label: 'ثبت و بررسی سند در همان دفتر' },
        { id: 'report', label: 'گزارش همان دفتر' },
      ],
      'book',
      'اینجا دفترها را می‌شناسی و در صورت مجوز مدیریت می‌کنی؛ دفتر، محدودهٔ ثبت و گزارش را مشخص می‌کند.',
      'هدف دفتر و مجوز استفاده یا مدیریت آن باید روشن باشد.',
      'هنگام ثبت سند و خواندن گزارش، دفتر انتخاب‌شده را دوباره کنترل کن؛ ارزش افزوده و مودیان بر دفتر قانونی متکی‌اند.',
      'support',
      'دفتر قانونی حذف یا غیرفعال نمی‌شود. ساخت یا انتخاب دفتر به معنی کپی خودکار تمام اسناد از دفتر دیگر نیست.',
    ),
    at(
      'report-book-selection',
      'مقایسه و خواندن گزارش‌های مالی',
      [
        { id: 'question', label: 'تعیین سؤال گزارش' },
        { id: 'book', label: 'تشخیص دفتر مبنا' },
        { id: 'filters', label: 'انتخاب تاریخ و فیلتر' },
        { id: 'report', label: 'خواندن گزارش قطعی' },
        { id: 'follow-up', label: 'بررسی تفاوت‌ها' },
      ],
      'book',
      'این صفحه مرجع شناخت نوع دفتر است تا اعداد دفتر قانونی و مدیریتی اشتباه مقایسه نشوند.',
      'باید بدانی گزارش برای کار قانونی است یا بررسی داخلی مدیریت.',
      'به گزارش موردنظر برگرد و تاریخ و دفتر مبنا را یکسان نگه دار؛ تفاوت ثبت‌ها را از ریز اسناد دنبال کن.',
      'support',
    ),
  ],
  'finance-dimensions': [
    at(
      'dimension-posting',
      'ثبت هزینه یا درآمد با جزئیات',
      [
        { id: 'dimension', label: 'تعریف سطح و مورد تفصیلی' },
        { id: 'account-rule', label: 'مجازکردن تفصیلی برای حساب' },
        { id: 'journal', label: 'انتخاب تفصیلی در سطر سند' },
        { id: 'posted', label: 'بررسی و ثبت قطعی' },
        { id: 'report', label: 'گزارش به تفکیک' },
      ],
      'dimension',
      'اینجا نام سطح و موردهای آن را آماده می‌کنی؛ مثلاً سطح مرکز هزینه و موردهای واحد فروش و پشتیبانی.',
      'نیاز گزارش‌گیری و نام‌گذاری یکنواخت پروژه‌ها یا مراکز هزینه باید مشخص شده باشد.',
      'در جزئیات حساب، قواعد تفصیلی را کنترل کن؛ سپس مورد درست را در سطر سند انتخاب کن تا گزارش تفکیکی قابل اتکا باشد.',
      'support',
      'تعریف تفصیلی، سندهای قبلی را خودکار به آن وصل نمی‌کند و به‌تنهایی هزینه یا درآمدی ثبت نمی‌کند.',
    ),
  ],
  'finance-fiscal-periods': [
    at(
      'fiscal-period-control',
      'کنترل ثبت‌های یک دورهٔ مالی',
      [
        { id: 'period', label: 'تعریف سال و دوره' },
        { id: 'operations', label: 'ثبت عملیات دوره' },
        { id: 'review', label: 'رسیدگی به اسناد معوق' },
        { id: 'close', label: 'بستن دورهٔ بررسی‌شده' },
        { id: 'next', label: 'ادامهٔ کار در دورهٔ مجاز' },
      ],
      'period',
      'این صفحه پشتیبان شروع و کنترل پایان دوره است؛ هم تعریف سال و دوره و هم بستن یا بازگشایی مجاز را پوشش می‌دهد.',
      'بازهٔ سال مالی و مسئول کنترل دوره باید مشخص باشد؛ قبل از بستن، اسناد باز و در انتظار بررسی را کنترل کن.',
      'ثبت‌های جدید را در دورهٔ مجاز ادامه بده؛ پایان سال را از فرایند جداگانهٔ بستن سال پیگیری کن.',
      'support',
      'نقش برجسته، آماده‌بودن دوره برای کار است و وضعیت زندهٔ آن را نشان نمی‌دهد. بازگشایی به دسترسی و علت معتبر نیاز دارد.',
    ),
  ],
  'finance-year-end': [
    at(
      'year-end-closing',
      'پایان سال و شروع سال بعد',
      [
        { id: 'review', label: 'کنترل اسناد و مانده‌های سال' },
        { id: 'preview', label: 'پیش‌نمایش و رفع موانع' },
        { id: 'prepare', label: 'تهیهٔ اسناد بستن و افتتاحیه' },
        { id: 'posted', label: 'بررسی و قطعی‌کردن اسناد' },
        { id: 'next', label: 'کنترل مانده‌های سال بعد' },
      ],
      'preview',
      'این صفحه عملیات هدایت‌شدهٔ پایان سال است؛ پارامترها، پیش‌نمایش، تهیهٔ اسناد و نتیجهٔ قطعی‌سازی را از همین‌جا پیگیری می‌کنی.',
      'سال مبدأ، در صورت نیاز سال بعد، حساب سود و زیان انباشته و کنترل اسناد و مانده‌ها لازم‌اند.',
      'اسناد پیشنهادی را پس از کنترل تهیه کن؛ وضعیت قطعی‌سازی و در صورت ایجاد افتتاحیه، مانده‌های سال بعد را بررسی کن.',
      'stage',
      'مرحلهٔ برجسته محل شروع کار صفحه است، نه وضعیت اجرای فعلی. تهیهٔ پیش‌نویس با ثبت قطعی فرق دارد؛ سند قطعی حذف یا ویرایش نمی‌شود.',
    ),
  ],
  'finance-fixed-assets': [
    at(
      'asset-depreciation',
      'از ثبت دارایی تا هزینهٔ استهلاک',
      [
        { id: 'purchase', label: 'مدرک تحصیل دارایی' },
        { id: 'asset', label: 'ثبت مشخصات و فعال‌سازی مالی' },
        { id: 'depreciation', label: 'محاسبهٔ استهلاک دوره' },
        { id: 'journal', label: 'بررسی سند مرتبط' },
        { id: 'report', label: 'کنترل دفتر دارایی و گزارش' },
      ],
      'asset',
      'اینجا دارایی را به دفتر اموال معرفی می‌کنی و ثبت مالی و استهلاک دوره‌ای آن را پیگیری می‌کنی.',
      'مدرک خرید یا تحصیل، دستهٔ دارایی، بهای تمام‌شده و عمر مفید تأییدشده لازم‌اند.',
      'وضعیت ثبت مالی دارایی و اجرای استهلاک را کنترل کن؛ سندهای مرتبط باید جداگانه بررسی و وضعیتشان پیگیری شود.',
      'stage',
      'خرید یک کالا به‌تنهایی به معنی ثبت‌شدن آن در دفتر دارایی ثابت نیست. این صفحه چند مرحلهٔ عمر دارایی را پوشش می‌دهد.',
    ),
  ],
  'finance-currencies': [
    at(
      'currency-revaluation',
      'از نرخ ارز تا سند تسعیر',
      [
        { id: 'currency', label: 'تعریف واحد پول' },
        { id: 'rate', label: 'ثبت نرخ معتبر با منبع' },
        { id: 'balance', label: 'کنترل مانده‌های ارزی' },
        { id: 'revaluation', label: 'اجرای تسعیر دوره' },
        { id: 'journal', label: 'بررسی سند پیش‌نویس تسعیر' },
      ],
      'revaluation',
      'این صفحه هم زیرساخت ارز و نرخ را نگه می‌دارد و هم محل اجرای تسعیر برای دورهٔ انتخاب‌شده است.',
      'ارز، نرخ معتبر و منبع آن، سال و دوره و مانده‌های ارزی مرتبط باید مشخص و کنترل شده باشند.',
      'نتیجهٔ تسعیر و سند پیش‌نویس را بررسی کن؛ ادامهٔ تأیید و ثبت قطعی از مسیر سند حسابداری پیگیری می‌شود.',
      'stage',
      'برجسته‌شدن تسعیر به معنی اجراشدن آن نیست. نرخ ثبت‌شده سابقهٔ تغییرناپذیر است و واحد ریال هر واحد ارز باید دقیق باشد.',
    ),
  ],
  'finance-cost-allocations': [
    at(
      'cost-allocation',
      'تقسیم هزینهٔ مشترک',
      [
        { id: 'rule', label: 'تعیین قاعده و مقصدها' },
        { id: 'balance', label: 'کنترل مانده و دوره' },
        { id: 'simulate', label: 'شبیه‌سازی و بررسی سهم‌ها' },
        { id: 'execute', label: 'اجرا و تهیهٔ سند پیش‌نویس' },
        { id: 'journal', label: 'بررسی و ثبت قطعی سند' },
      ],
      'simulate',
      'اینجا قاعدهٔ تقسیم را آماده و نتیجه را پیش از اجرا کنترل می‌کنی؛ اجرای نهایی هم از همین صفحه پیگیری می‌شود.',
      'قاعده و نسبت‌ها، حساب‌های مبدأ و مقصد و دورهٔ مربوط باید تأیید و ماندهٔ قابل تسهیم مشخص شده باشند.',
      'پس از بررسی شبیه‌سازی، اجرای مجاز را انجام بده و سند پیش‌نویس ساخته‌شده را برای تأیید و ثبت قطعی دنبال کن.',
      'stage',
      'شبیه‌سازی سند نمی‌سازد؛ مرحلهٔ برجسته نقش این صفحه را نشان می‌دهد و به معنی تأیید یا اجرای قاعده نیست.',
    ),
  ],
  'finance-reports': [
    {
      id: 'financial-report-directory',
      title: 'از ثبت عملیات تا گزارش مالی',
      steps: REPORT_STEPS,
      role: 'overview',
      position:
        'این صفحه فهرست مسیرهای گزارش‌گیری است؛ گزارش موردنیاز را انتخاب می‌کنی و وارد فیلترها و ریز نتیجه می‌شوی.',
      before:
        'سؤال گزارش، دفتر مبنا و بازهٔ موردنظر باید روشن باشند؛ گزارش‌های قطعی به ثبت قطعی اسناد متکی‌اند.',
      after:
        'گزارش مناسب را باز کن؛ برای اختلاف، ریز حساب یا سندهای مرتبط را بررسی و از مسیر مجاز اصلاح پیگیری کن.',
      note: 'مانده، گردش، سود و جریان نقد، سؤال‌های متفاوتی را جواب می‌دهند؛ این صفحه خودش ثبت یا اصلاح مالی انجام نمی‌دهد.',
    },
  ],
  'finance-trial-balance': financialReport(
    'trial-balance-check',
    'کنترل ماندهٔ حساب‌ها',
    'اینجا پس از ثبت قطعی اسناد، ماندهٔ حساب‌ها را تا یک تاریخ بررسی می‌کنی.',
    'دفتر و تاریخ مبنا و سندهای قطعی مربوط باید مشخص باشند.',
    'اگر مانده‌ای نامنتظره است، از کدینگ، دفتر حساب یا سندهای مرتبط علتش را دنبال کن؛ تراز بودن به‌تنهایی درستی همهٔ حساب‌ها نیست.',
  ),
  'finance-trial-balance-columns': financialReport(
    'trial-balance-columns-check',
    'کنترل گردش و ماندهٔ دوره',
    'اینجا گردش و ماندهٔ ثبت‌های قطعی را با قالب دو، چهار، شش یا هشت‌ستونی کنار هم می‌بینی.',
    'دفتر، سطح حساب، قالب گزارش و تاریخ‌های مبنا باید انتخاب شده باشند.',
    'موارد خلاف ماهیت یا اختلاف گردش را از دفتر حساب و سندهای مربوط بررسی کن؛ قبل از مقایسه، عنوان ستون‌ها را یکسان بگیر.',
  ),
  'finance-ledger': [
    at(
      'ledger-investigation',
      'رسیدگی از یک مانده تا سند اولیه',
      [
        { id: 'report', label: 'یافتن مانده یا اختلاف در گزارش' },
        { id: 'ledger', label: 'بررسی ریز گردش حساب' },
        { id: 'journal', label: 'بازکردن سند مؤثر' },
        { id: 'source', label: 'تطبیق با مدرک منبع' },
        { id: 'follow-up', label: 'پیگیری اصلاح مجاز' },
      ],
      'ledger',
      'این صفحه پل بین جمع یک گزارش و سندهای مؤثر بر آن است؛ ریز کل، معین یا تفصیلی را نشان می‌دهد.',
      'حساب یا تفصیلی موردنظر، دفتر و بازهٔ بررسی باید مشخص باشند؛ گردش گزارش از اسناد قطعی می‌آید.',
      'سند غیرمنتظره را باز و با منبع مقایسه کن؛ اگر اصلاح لازم است، سند قطعی فقط از مسیر برگشت و آثار جبرانی اصلاح می‌شود.',
      'support',
      'بازکردن دفتر حساب، ثبت قبلی را تغییر نمی‌دهد؛ ماندهٔ جاری هر سطر با مبلغ همان سطر فرق دارد.',
    ),
  ],
  'finance-income-statement': financialReport(
    'income-statement-review',
    'از درآمد و هزینه تا نتیجهٔ دوره',
    'اینجا نتیجهٔ درآمد و هزینهٔ ثبت‌شدهٔ یک دوره را بررسی می‌کنی؛ مرحلهٔ تحلیل پس از قطعی‌شدن سندهاست.',
    'ثبت‌های درآمد و هزینه باید در حساب و دفتر درست قطعی شده و بازهٔ گزارش مشخص باشد.',
    'برای علت سود یا زیان، حساب‌های درآمد و هزینه و ریز سندها را بررسی کن؛ برای جریان واقعی پول، گزارش نقد و خزانه را جدا ببین.',
  ),
  'finance-balance-sheet': financialReport(
    'balance-sheet-review',
    'از ثبت‌های قطعی تا وضعیت مالی',
    'اینجا وضعیت دارایی، بدهی و حقوق صاحبان سهام را در یک تاریخ بررسی می‌کنی.',
    'دفتر و تاریخ گزارش و دسته‌بندی درست حساب‌ها باید کنترل شده باشند؛ مبنا، اسناد قطعی است.',
    'اختلاف یا ماندهٔ غیرمنتظره را از تراز و دفتر حساب دنبال کن؛ برای مقایسه با تاریخ دیگر، دفتر و مبنای گزارش را ثابت نگه دار.',
  ),
  'finance-cash-flow': financialReport(
    'cash-flow-review',
    'از ثبت‌های قطعی تا تحلیل جریان نقد',
    'اینجا جریان نقد دوره را در فعالیت‌های عملیاتی، سرمایه‌گذاری و تأمین مالی بررسی می‌کنی.',
    'ثبت‌های قطعی مرتبط با وجه نقد، دفتر و بازهٔ گزارش باید مشخص و کنترل شده باشند.',
    'تفاوت نقد ابتدا و انتهای دوره را بررسی و موارد نامنتظره را با خزانه، بانک و ریز سندها تطبیق بده.',
    'این صورت مالی پیش‌بینی نقدینگی یا اجرای پرداخت نیست و با نمودار گردش اخیر خزانه یکسان نیست.',
  ),
  'finance-profitability': [
    at(
      'dimension-profitability',
      'از ثبت تفصیلی تا تصمیم دربارهٔ سودآوری',
      [
        { id: 'dimension', label: 'تعریف تفصیلی یا بُعد مرتبط' },
        { id: 'journal', label: 'ثبت درست درآمد و هزینه' },
        { id: 'posted', label: 'بررسی و ثبت قطعی سندها' },
        { id: 'profitability', label: 'گزارش سودآوری به تفکیک' },
        { id: 'follow-up', label: 'بررسی علت و اقدام مدیریتی' },
      ],
      'profitability',
      'اینجا نتیجهٔ ثبت‌های قطعی را به تفکیک بُعد انتخاب‌شده، مثل پروژه یا مرکز هزینه، بررسی می‌کنی.',
      'ابعاد مرتبط و ثبت درست جزئیات درآمد و هزینه، دفتر و بازهٔ گزارش باید بررسی شده باشند.',
      'برای نتیجهٔ غیرمنتظره، کامل‌بودن ثبت‌ها و تفصیلی‌های سطرها را کنترل کن؛ پس از روشن‌شدن علت، تصمیم مدیریتی بگیر.',
      'stage',
      'نبود دادهٔ کافی برای یک بُعد به معنی نبود درآمد یا زیان‌ده بودن آن نیست؛ کیفیت گزارش به دادهٔ ثبت‌شده وابسته است.',
    ),
  ],
  'finance-assistant': [
    at(
      'finance-assisted-analysis',
      'کمک به بررسی یک سؤال مالی',
      [
        { id: 'question', label: 'تعیین سؤال و بازه' },
        { id: 'data', label: 'کنترل دسترسی و دادهٔ مبنا' },
        { id: 'assistant', label: 'درخواست قابل‌استفاده از یار مالی' },
        { id: 'verify', label: 'تطبیق پاسخ با اسناد اصلی' },
        { id: 'action', label: 'اقدام از مسیر مجاز' },
      ],
      'assistant',
      'یار مالی ابزار کمکی بررسی است؛ در این صفحه درخواست‌های موجود و وضعیت امکان اجرای آن‌ها را می‌بینی.',
      'سؤال، بازه، دفتر و دادهٔ مجاز باید روشن باشند؛ قابلیت درخواست و اعتبار لازم را بررسی کن.',
      'اگر درخواست قابل اجراست، نتیجه را با گزارش و رکورد اصلی تطبیق بده؛ هر ثبت مالی از مسیر و دسترسی مجاز خودش انجام می‌شود.',
      'support',
      'پاسخ یا پیشنهاد یار به معنی ایجاد، تأیید یا ثبت قطعی سند نیست؛ نتیجه و وضعیت واقعی عملیات را ملاک قرار بده.',
    ),
  ],
};
